import { useQuery } from '@tanstack/react-query';
import {
  Archive,
  Check,
  Circle,
  Link as LinkIcon,
  Pencil,
  Shield,
  Trash2,
  User,
} from 'lucide-react';
import { createElement } from 'react';
import { listActivity, type Artifact, type ArtifactActivity } from '@/lib/api';
import { formatArtifactActivity, type ArtifactActivityIcon } from '@/lib/artifact-activity-format';

interface ArtifactActivityPanelProps {
  artifact: Artifact;
}

const ICON_MAP: Record<ArtifactActivityIcon, React.ComponentType<{ className?: string }>> = {
  circle: Circle,
  check: Check,
  archive: Archive,
  edit: Pencil,
  user: User,
  shield: Shield,
  link: LinkIcon,
  trash: Trash2,
};

function formatTimestamp(timestamp: string): string {
  return timestamp.slice(0, 19).replace('T', ' ');
}

export function ArtifactActivityPanel({
  artifact,
}: ArtifactActivityPanelProps): React.ReactElement {
  const query = useQuery<ArtifactActivity[]>({
    queryKey: ['artifact', artifact.id, 'activity'],
    queryFn: () => listActivity(artifact.id, { limit: 50 }),
  });

  if (query.isLoading) {
    return <div className="text-xs text-text-secondary">Loading activity…</div>;
  }

  if (query.isError) {
    return (
      <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
        {query.error.message}
      </div>
    );
  }

  const rows = query.data ?? [];
  if (rows.length === 0) {
    return <div className="text-xs text-text-secondary">No activity yet.</div>;
  }

  return (
    <ol className="flex flex-col gap-2 text-sm">
      {rows.map(row => {
        const formatted = formatArtifactActivity(row);
        const iconComponent = ICON_MAP[formatted.icon];

        return (
          <li key={row.id} className="flex items-start gap-2">
            {createElement(iconComponent, {
              className: 'mt-0.5 size-4 shrink-0 text-text-secondary',
            })}
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span className="text-text-primary">{formatted.label}</span>
                <span className="font-mono text-text-secondary">
                  {formatTimestamp(row.createdAt)}
                </span>
                {(row.actorLogin ?? row.actorUserId) && (
                  <span className="font-mono text-text-secondary">
                    by {row.actorLogin ?? row.actorUserId}
                  </span>
                )}
              </div>
              {formatted.detailText && (
                <span className="break-words text-xs text-text-secondary">
                  {formatted.detailText}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
